import { Card, Dialog } from '@mui/material';
import { Box, Stack } from '@mui/system';
import {
  Button,
  CreateButton,
  Datagrid,
  DeleteButton,
  EditButton,
  FunctionField,
  List,
  required,
  TextField,
  TextInput,
  useNotify,
  useRedirect,
  useTranslate,
} from 'react-admin';
import { ReactElement, useMemo, useState } from 'react';
import { CgCreate, CgCreateProps, CgEdit, CgEditProps } from '../../components/common';
import { Resources } from '../resource';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { EmptyContent } from 'src/components/empty-content';
import { PermissionGuard } from 'src/routes/components';
import AuditLogDialog from '../../components/AuditLog/AuditLogDialog';
import { XButton } from '../../components/XButton';
import { Iconify } from 'src/components/iconify';
import resources from '..';
import { useSyncNats } from 'src/hooks/use-sync-nats';

interface CrawlerFormProp {
  isCreate: boolean;
}

const CrawlerForm = (prop: CrawlerFormProp) => {
  const { isCreate } = prop;
  return (
    <Box sx={{ pt: 3, pb: 3 }}>
      <Stack>
        <TextInput
          readOnly={!isCreate}
          source="key"
          label="module.crawlerMonitor.key"
          validate={[required()]}
        />
        <TextInput
          source="value"
          rows={10}
          multiline
          label="module.crawlerMonitor.value"
          validate={[required()]}
        />
      </Stack>
    </Box>
  );
};

const EditPopup = (props: CgEditProps) => {
  const redirect = useRedirect();
  const notify = useNotify();
  const t = useTranslate();

  const onSuccess = () => {
    const currentUrl = window.location.href;
    const origin = window.location.origin;
    const redirectValue = currentUrl.replace(origin, '');

    notify(t('module.agency.validate.updateSuccess'), { type: 'success', autoHideDuration: 1000 });

    if (props.onClose) {
      props.onClose();
    }
    if (props.resource) {
      redirect(redirectValue, props.resource);
    }
  };

  const onError = (error: any) => {
    const errorCode = error?.body?.code;
    const notifyError = (msg: string) => {
      notify(msg, { type: 'error' });
    };

    switch (errorCode) {
      case 'CRAWLER_MONITOR_NOT_FOUND':
        notifyError(t('module.crawlerMonitor.validate.crawlerMonitorNotFound'));
        break;
      case 'CRAWLER_MONITOR_ID_REQUIRED':
        notifyError(t('module.crawlerMonitor.validate.crawlerMonitorIdRequired'));
        break;
      case 'INTERNAL_SERVER_ERROR':
        notifyError(error?.message || t('module.crawlerMonitor.unknownErrorUpdate'));
        break;
      default:
        notifyError(t('module.crawlerMonitor.unknownErrorUpdate'));
        break;
    }
  };
  return (
    <CgEdit
      {...props}
      title="module.crawlerMonitor.edit"
      resource={Resources.crawlerMonitor}
      onClose={props.onClose}
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess, onError }}
    >
      <CrawlerForm isCreate={false} />
    </CgEdit>
  );
};

const CreatePopup = (props: CgCreateProps) => {
  const redirect = useRedirect();
  const notify = useNotify();
  const t = useTranslate();

  const onSuccess = () => {
    const currentUrl = window.location.href;
    const origin = window.location.origin;
    const redirectValue = currentUrl.replace(origin, '');

    notify(t('module.agency.validate.updateSuccess'), { type: 'success', autoHideDuration: 1000 });

    if (props.onClose) {
      props.onClose();
    }
    if (props.resource) {
      redirect(redirectValue, props.resource);
    }
  };

  const onError = (error: any) => {
    const errorCode = error?.body?.code;
    const notifyError = (msg: string) => {
      notify(msg, { type: 'error' });
    };

    switch (errorCode) {
      case 'KEY_REQUIRED':
        notifyError(t('module.crawlerMonitor.validate.keyRequired'));
        break;
      case 'VALUE_REQUIRED':
        notifyError(t('module.crawlerMonitor.validate.valueRequired'));
        break;
      case 'CRAWLER_MONITOR_KEY_VALUE_EXISTS':
        notifyError(t('module.crawlerMonitor.validate.crawlerMonitorKeyValueExists'));
        break;
      case 'INTERNAL_SERVER_ERROR':
        notifyError(error?.message || t('module.crawlerMonitor.unknownError'));
        break;
      default:
        notifyError(t('module.crawlerMonitor.unknownError'));
    }
  };

  return (
    <CgCreate
      {...props}
      title="module.crawlerMonitor.create"
      resource={Resources.crawlerMonitor}
      onClose={props.onClose}
      mutationOptions={{ onSuccess, onError }}
    >
      <CrawlerForm isCreate />
    </CgCreate>
  );
};

const View = () => {
  const [popup, setPopup] = useState<ReactElement>();
  const translate = useTranslate();
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditTitle, setAuditTitle] = useState('Audit Logs');
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const { handleSync } = useSyncNats();
  const [syncing, setSyncing] = useState(false);

  const handleSyncNats = async () => {
    setSyncing(true);
    try {
      await handleSync(Resources.crawlerMonitor);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={translate('module.crawlerMonitor.title')}
        action={
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            mb={1}
          >
            <PermissionGuard resource="crawler-monitor" action="create" fallback={<></>}>
              <CreateButton
                size="medium"
                variant="contained"
                onClick={(e) => {
                  e.preventDefault();
                  setPopup(<CreatePopup onClose={() => setPopup(undefined)} />);
                }}
              />
            </PermissionGuard>
            <PermissionGuard resource="crawler-monitor" action="update" fallback={<></>}>
              <Button
                variant="contained"
                size="medium"
                color="info"
                disabled={syncing}
                onClick={handleSyncNats}
                label={translate('module.natsSync.sync')}
              >
                <Iconify icon="mdi:cloud-sync" />
              </Button>
            </PermissionGuard>
            <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
              <Button
                variant="contained"
                size="medium"
                color="secondary"
                onClick={() => {
                  setSelectedRecord({});
                  setAuditTitle(`${translate('resource.auditLogs')} - ${translate('module.crawlerMonitor.title')}`);
                  setAuditLogOpen(true);
                }}
                label={translate('resource.auditLogs')}
              >
                <Iconify icon="ph:note" />
              </Button>
            </PermissionGuard>
          </Stack>
        }
      />
      <List exporter={false} actions={false} empty={false} pagination={false}>
        <Card>
          <Datagrid bulkActionButtons={false} rowClick="expand" empty={<EmptyContent />}>
            <TextField
              source="key"
              label="module.crawlerMonitor.key"
              sx={{
                whiteSpace: { xs: 'normal', sm: 'nowrap', md: 'nowrap' },
                wordBreak: 'break-all',
              }}
            />
            <TextField
              source="value"
              label="module.crawlerMonitor.value"
              sx={{
                whiteSpace: 'normal',
                wordBreak: 'break-all',
                display: { xs: 'none', sm: 'block', md: 'block' },
              }}
            />
            <FunctionField
              textAlign="left"
              render={(record: any) => (
                <Stack direction="row" spacing={4} sx={{ justifyContent: 'flex-end' }}>
                  <PermissionGuard resource="crawler-monitor" action="update" fallback={<></>}>
                    <EditButton
                      onClick={(e) => {
                        e.preventDefault();
                        setPopup(<EditPopup id={record.id} onClose={() => setPopup(undefined)} />);
                      }}
                    />
                  </PermissionGuard>
                  <PermissionGuard resource="crawler-monitor" action="delete" fallback={<></>}>
                    <DeleteButton mutationMode="pessimistic" />
                  </PermissionGuard>
                  <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                    <XButton
                      variant="text"
                      size="medium"
                      color="secondary"
                      disabled={!record.hasAuditLog}
                      onClick={() => {
                        setSelectedRecord(record);
                        setAuditTitle(`${translate('resource.auditLogs')} - (#${record.id})`);
                        setAuditLogOpen(true);
                      }}
                      label={translate('resource.auditLogs')}
                    >
                      <Iconify icon="ph:note" />
                    </XButton>
                  </PermissionGuard>
                </Stack>
              )}
            />
          </Datagrid>
        </Card>
      </List>
      <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="sm" fullWidth>
        {popup}
      </Dialog>
      <AuditLogDialog
        open={auditLogOpen}
        onClose={() => setAuditLogOpen(false)}
        resource={Resources.crawlerMonitor}
        recordId={selectedRecord?.id}
        title={auditTitle}
      />
    </DashboardContent>
  );
};

export default View;
